import React, { useState } from 'react';
import TrebbleButton from '../common/TrebbleButton';
import TrebbleInput from '../common/TrebbleInput';
import TrebbleRadio from '../common/TrebbleRadio';
import TrebbleTooltip from '../common/TrebbleTooltip';
import TrebbleCard from '../common/TrebbleCard';
import ModalHeader from '../common/ModalHeader';
import { GuestAccessLevel } from '../../models/collaboration/types/GuestTypes';

interface GuestInviteContentProps {
    mode: 'invite' | 'manage';
    currentGuestEmail?: string;
    currentGuestName?: string;
    accessLevel?: GuestAccessLevel;
    canSwap: boolean;
    onInvite: (email: string, accessLevel: GuestAccessLevel) => Promise<void>;
    onRevoke: () => Promise<void>;
    onClose: () => void;
    i18nModule: Record<string, string>;
}

const GuestInviteContent: React.FC<GuestInviteContentProps> = ({
    mode,
    currentGuestEmail,
    currentGuestName,
    accessLevel: initialAccessLevel,
    canSwap,
    onInvite,
    onRevoke,
    onClose,
    i18nModule,
}) => {
    const [email, setEmail] = useState('');
    const [accessLevel, setAccessLevel] = useState<GuestAccessLevel>(initialAccessLevel || GuestAccessLevel.CanEdit);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showChangeGuest, setShowChangeGuest] = useState(false);

    const t = (key: string): string => {
        return (window as unknown as Record<string, Function>).getI18n(i18nModule, key);
    };

    const handleInvite = async () => {
        if (!email.trim()) {
            return;
        }
        setIsLoading(true);
        setErrorMessage(null);
        try {
            await onInvite(email.trim(), accessLevel);
        } catch (error: unknown) {
            if (error && typeof error === 'object' && (error as { handledByUpgradePrompt?: boolean }).handledByUpgradePrompt) {
                return;
            }
            const message = error instanceof Error ? error.message : t('INVITE_ERROR');
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRevoke = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            await onRevoke();
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : t('REVOKE_ERROR');
            setErrorMessage(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && email.trim()) {
            handleInvite();
        }
    };

    const renderInviteForm = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <ModalHeader
                title={t('INVITE_A_GUEST')}
                marginBottom={0}
            />

            <TrebbleInput
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('GUEST_EMAIL_PLACEHOLDER')}
                useEditorTheme={false}
                disabled={isLoading}
            />

            <div>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                    {t('ACCESS_LEVEL')}
                </div>
                <TrebbleRadio.Group
                    value={accessLevel}
                    onChange={(e) => setAccessLevel(e.target.value as GuestAccessLevel)}
                    disabled={isLoading}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <TrebbleRadio value="can_edit" useEditorTheme={false}>
                            {t('CAN_EDIT')}
                        </TrebbleRadio>
                        <TrebbleRadio value="can_view" useEditorTheme={false}>
                            {t('CAN_VIEW')}
                        </TrebbleRadio>
                    </div>
                </TrebbleRadio.Group>
            </div>

            <div style={{ fontSize: '12px', color: '#888' }}>
                {t('GUEST_CAPABILITIES_NOTE')}
            </div>

            {errorMessage && (
                <div style={{ color: '#ff4d4f', fontSize: '13px' }}>
                    {errorMessage}
                </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <TrebbleButton
                    onClick={onClose}
                    useEditorTheme={false}
                    disabled={isLoading}
                >
                    {t('REVOKE_CONFIRM_CANCEL')}
                </TrebbleButton>
                <TrebbleButton
                    type="primary"
                    onClick={handleInvite}
                    useEditorTheme={false}
                    loading={isLoading}
                    disabled={!email.trim()}
                >
                    {t('SEND_INVITE')}
                </TrebbleButton>
            </div>
        </div>
    );

    const renderManageView = () => {
        if (showChangeGuest) {
            return renderInviteForm();
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <ModalHeader
                    title={t('CURRENT_GUEST')}
                    marginBottom={0}
                />

                <div style={{ padding: '12px 0', borderBottom: '1px solid #e8e8e8' }}>
                    <div style={{ fontSize: '14px', fontWeight: 500 }}>
                        {currentGuestName || currentGuestEmail}
                    </div>
                    {currentGuestName && currentGuestEmail && (
                        <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                            {currentGuestEmail}
                        </div>
                    )}
                    <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                        {initialAccessLevel === 'can_edit' ? t('CAN_EDIT') : t('CAN_VIEW')}
                    </div>
                </div>

                {errorMessage && (
                    <div style={{ color: '#ff4d4f', fontSize: '13px' }}>
                        {errorMessage}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <TrebbleButton
                        onClick={handleRevoke}
                        useEditorTheme={false}
                        loading={isLoading}
                        danger
                    >
                        {t('REVOKE_ACCESS')}
                    </TrebbleButton>
                    <TrebbleTooltip
                        title={!canSwap ? t('CHANGE_GUEST_DISABLED_TOOLTIP') : ''}
                        useEditorTheme={false}
                    >
                        <span>
                            <TrebbleButton
                                type="primary"
                                onClick={() => setShowChangeGuest(true)}
                                useEditorTheme={false}
                                disabled={!canSwap || isLoading}
                            >
                                {t('CHANGE_GUEST')}
                            </TrebbleButton>
                        </span>
                    </TrebbleTooltip>
                </div>
            </div>
        );
    };

    return (
        <TrebbleCard
            useEditorTheme={false}
            style={{ padding: '24px' }}
        >
            {mode === 'invite' ? renderInviteForm() : renderManageView()}
        </TrebbleCard>
    );
};

export { GuestInviteContent };
export type { GuestInviteContentProps };
